const SECTION_SELECTOR = "[data-hp-routine-section]";
const TAB_SELECTOR = "[data-hp-routine-tab]";
const PANEL_SELECTOR = "[data-hp-routine-panel]";
const SCROLLER_SELECTOR = "[data-hp-routine-scroller]";
const CONTROLS_SELECTOR = "[data-hp-routine-controls]";
const PROGRESS_SELECTOR = "[data-hp-routine-progress]";
const PREV_SELECTOR = "[data-hp-routine-prev]";
const NEXT_SELECTOR = "[data-hp-routine-next]";
const ACTIVE_CLASS = "is-active";
const STEP_SELECTOR = ".hp-routine-step";
const CTA_SELECTOR = "[data-hp-routine-cta]";
const CTA_HOME_SELECTOR = "[data-hp-routine-cta-home]";
const CTA_SLOT_SELECTOR = "[data-hp-routine-cta-slot]";
const TABS_TRACK_SELECTOR = "[data-hp-routine-tabs-track]";

/** Per-step accent from theme editor. Desktop hover uses this in CSS. */
const ACCENT_VAR = "--hp-routine-disc-accent";

/** Mobile only: applied on the snapped step so the disc picks up its accent. */
const DISC_ACTIVE_VAR = "--hp-routine-disc-active";

const SCROLL_EDGE_PX = 10;
const SCROLL_SETTLE_MS = 100;

const mobileLayoutMq = window.matchMedia("(max-width: 749px)");

/** @type {Record<string, number>} */
const KEY_TO_DIRECTION = {
  ArrowLeft: -1,
  ArrowUp: -1,
  ArrowRight: 1,
  ArrowDown: 1,
};

/** @type {WeakMap<HTMLElement, AbortController>} */
const sectionControllers = new WeakMap();

/**
 * @returns {boolean}
 */
function isMobileLayout() {
  return mobileLayoutMq.matches;
}

/**
 * Tabs live in the toolbar track at every breakpoint. Desktop lays that track
 * out beside the secondary CTA. Mobile scrolls the same track as one row.
 *
 * @param {HTMLElement} section
 */
function placeRoutineTabs(section) {
  const track = section.querySelector(TABS_TRACK_SELECTOR);
  if (!(track instanceof HTMLElement)) return;

  section.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
    if (tab instanceof HTMLElement && tab.parentElement !== track) {
      track.appendChild(tab);
    }
  });
}

/**
 * Desktop: secondary CTA lives in the toolbar beside tabs. Mobile: move it above
 * the active panel scrollbar so it sits between the product card and progress track.
 *
 * @param {HTMLElement} section
 */
function placeRoutineCta(section) {
  const cta = section.querySelector(CTA_SELECTOR);
  if (!(cta instanceof HTMLElement)) return;

  if (isMobileLayout()) {
    const activePanel = section.querySelector(
      `${PANEL_SELECTOR}:not([hidden])`,
    );
    if (!(activePanel instanceof HTMLElement)) return;
    const slot = activePanel.querySelector(CTA_SLOT_SELECTOR);
    if (slot instanceof HTMLElement && cta.parentElement !== slot) {
      slot.appendChild(cta);
    }
    return;
  }

  const home = section.querySelector(CTA_HOME_SELECTOR);
  if (home instanceof HTMLElement && cta.parentElement !== home) {
    home.appendChild(cta);
  }
}

/**
 * @param {HTMLElement} section
 */
function placeRoutineLayout(section) {
  placeRoutineTabs(section);
  placeRoutineCta(section);
}

/**
 * @param {HTMLElement} step
 * @param {HTMLElement} scroller
 * @returns {number}
 */
function stepOffsetRelativeToScroller(step, scroller) {
  let pos = 0;
  /** @type {HTMLElement | null} */
  let el = step;
  while (el && el !== scroller) {
    pos += el.offsetLeft;
    /** @type {Element | null} */
    const op = el.offsetParent;
    el = op instanceof HTMLElement ? op : null;
  }
  return pos;
}

/**
 * @param {HTMLElement} scroller
 * @param {Element[]} steps
 * @returns {number}
 */
function getRoutineDiscIndexFromScroll(scroller, steps) {
  const n = steps.length;
  if (n <= 1) return 0;

  const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  if (maxScroll <= SCROLL_EDGE_PX) return 0;

  const sl = scroller.scrollLeft;

  if (getComputedStyle(scroller).direction !== "rtl") {
    if (sl >= maxScroll - SCROLL_EDGE_PX) return n - 1;
    if (sl <= SCROLL_EDGE_PX) return 0;
  }

  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < n; i++) {
    const step = steps[i];
    if (!(step instanceof HTMLElement)) continue;
    const pos = stepOffsetRelativeToScroller(step, scroller);
    const dist = Math.abs(pos - sl);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * @param {HTMLElement} panel
 */
function clearDiscActiveOnAllSteps(panel) {
  panel.querySelectorAll(STEP_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) el.style.removeProperty(DISC_ACTIVE_VAR);
  });
}

/**
 * @param {HTMLElement} panel
 * @param {HTMLElement | null | undefined} stepEl
 */
function applyMobileDiscForStep(panel, stepEl) {
  clearDiscActiveOnAllSteps(panel);
  if (!(stepEl instanceof HTMLElement)) return;
  const accent = getComputedStyle(stepEl).getPropertyValue(ACCENT_VAR).trim();
  if (accent) stepEl.style.setProperty(DISC_ACTIVE_VAR, accent);
}

/**
 * @param {HTMLElement} panel
 */
function applyMobileDiscFromScroll(panel) {
  if (!isMobileLayout()) {
    clearDiscActiveOnAllSteps(panel);
    return;
  }
  const scroller = panel.querySelector(SCROLLER_SELECTOR);
  if (!(scroller instanceof HTMLElement)) return;
  const steps = [...panel.querySelectorAll(STEP_SELECTOR)];
  if (steps.length === 0) return;
  const idx = getRoutineDiscIndexFromScroll(scroller, steps);
  const step = steps[idx];
  if (step instanceof HTMLElement) applyMobileDiscForStep(panel, step);
}

/**
 * @param {HTMLElement} section
 * @returns {{ tabs: HTMLElement[], panels: HTMLElement[] }}
 */
function getRoutineParts(section) {
  return {
    tabs: [...section.querySelectorAll(TAB_SELECTOR)].filter(
      (tab) => tab instanceof HTMLElement,
    ),
    panels: [...section.querySelectorAll(PANEL_SELECTOR)].filter(
      (panel) => panel instanceof HTMLElement,
    ),
  };
}

/**
 * @param {HTMLElement} panel
 */
function updatePanelProgress(panel) {
  const scroller = panel.querySelector(SCROLLER_SELECTOR);
  const controls = panel.querySelector(CONTROLS_SELECTOR);
  const progress = panel.querySelector(PROGRESS_SELECTOR);
  const prev = panel.querySelector(PREV_SELECTOR);
  const next = panel.querySelector(NEXT_SELECTOR);
  if (
    !(scroller instanceof HTMLElement) ||
    !(controls instanceof HTMLElement) ||
    !(progress instanceof HTMLElement)
  )
    return;

  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  const canScroll = maxScroll > 1;

  controls.hidden = !canScroll;
  if (!canScroll) {
    progress.style.width = "100%";
    return;
  }

  const scrollLeft = scroller.scrollLeft;
  if (prev instanceof HTMLButtonElement) {
    prev.disabled = scrollLeft <= 0;
  }
  if (next instanceof HTMLButtonElement) {
    next.disabled = scrollLeft >= maxScroll - 1;
  }

  const ratio = Math.min(scrollLeft / maxScroll, 1);
  const viewRatio = scroller.clientWidth / scroller.scrollWidth;
  const barWidth = viewRatio + ratio * (1 - viewRatio);
  progress.style.width = `${(barWidth * 100).toFixed(2)}%`;
}

/**
 * @param {HTMLElement} panel
 * @param {AbortSignal} signal
 */
function bindPanelControls(panel, signal) {
  const scroller = panel.querySelector(SCROLLER_SELECTOR);
  const prev = panel.querySelector(PREV_SELECTOR);
  const next = panel.querySelector(NEXT_SELECTOR);
  if (!(scroller instanceof HTMLElement)) return;

  scroller.addEventListener("scroll", () => updatePanelProgress(panel), {
    passive: true,
    signal,
  });

  const getStepWidth = () => {
    const firstStep = scroller.querySelector(".hp-routine-step");
    return firstStep instanceof HTMLElement ? firstStep.offsetWidth : 300;
  };

  if (prev instanceof HTMLElement) {
    prev.addEventListener(
      "click",
      () => scroller.scrollBy({ left: -getStepWidth(), behavior: "smooth" }),
      { signal },
    );
  }
  if (next instanceof HTMLElement) {
    next.addEventListener(
      "click",
      () => scroller.scrollBy({ left: getStepWidth(), behavior: "smooth" }),
      { signal },
    );
  }

  let settleTimer = 0;
  const settleMobileDisc = () => {
    if (!isMobileLayout()) return;
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(() => {
      applyMobileDiscFromScroll(panel);
    }, SCROLL_SETTLE_MS);
  };

  scroller.addEventListener("scroll", settleMobileDisc, {
    passive: true,
    signal,
  });
  scroller.addEventListener(
    "scrollend",
    () => {
      window.clearTimeout(settleTimer);
      if (isMobileLayout()) applyMobileDiscFromScroll(panel);
    },
    { signal },
  );

  mobileLayoutMq.addEventListener(
    "change",
    () => {
      applyMobileDiscFromScroll(panel);
    },
    { signal },
  );

  requestAnimationFrame(() => {
    applyMobileDiscFromScroll(panel);
  });
}

/**
 * @param {HTMLElement} section
 * @param {HTMLElement} activeTab
 * @param {boolean} focusTab
 * @param {boolean} scrollTab Scroll the tab into the mobile row. Skip this for the
 * selection that runs when the section first binds, or the page jumps down to it.
 */
function selectRoutineTab(
  section,
  activeTab,
  focusTab = false,
  scrollTab = false,
) {
  const { tabs, panels } = getRoutineParts(section);
  const panelID = activeTab.getAttribute("aria-controls");

  tabs.forEach((tab) => {
    const isActive = tab === activeTab;
    tab.classList.toggle(ACTIVE_CLASS, isActive);
    tab.setAttribute("aria-selected", String(isActive));
    tab.setAttribute("tabindex", isActive ? "0" : "-1");
  });

  panels.forEach((panel) => {
    const isActive = panel.id === panelID;
    panel.classList.toggle(ACTIVE_CLASS, isActive);
    panel.hidden = !isActive;
  });

  if (focusTab) activeTab.focus();

  const activePanel = panels.find((p) => p.id === panelID);
  if (activePanel) {
    const panelToUpdate = activePanel;
    requestAnimationFrame(() => {
      updatePanelProgress(panelToUpdate);
      applyMobileDiscFromScroll(panelToUpdate);
      placeRoutineLayout(section);
      if (scrollTab && isMobileLayout()) {
        activeTab.scrollIntoView({ inline: "nearest", block: "nearest" });
      }
    });
  }
}

/**
 * @param {HTMLElement[]} tabs
 * @param {HTMLElement} currentTab
 * @param {number} direction
 * @returns {HTMLElement}
 */
function getAdjacentTab(tabs, currentTab, direction) {
  const currentIndex = tabs.indexOf(currentTab);
  if (currentIndex === -1) return tabs[0] || currentTab;
  const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
  return tabs[nextIndex] || currentTab;
}

/**
 * @param {HTMLElement} section
 */
function bindRoutineSection(section) {
  if (sectionControllers.has(section)) return;

  const controller = new AbortController();
  sectionControllers.set(section, controller);

  const { tabs, panels } = getRoutineParts(section);
  if (tabs.length === 0) return;

  panels.forEach((panel) => bindPanelControls(panel, controller.signal));

  const selectedTab = tabs.find(
    (tab) => tab.getAttribute("aria-selected") === "true",
  );
  placeRoutineLayout(section);

  if (selectedTab) {
    selectRoutineTab(section, selectedTab);
  } else {
    const firstTab = tabs[0];
    if (firstTab) selectRoutineTab(section, firstTab);
  }

  const resizeObserver = new ResizeObserver(() => {
    const activePanel = panels.find((p) => !p.hidden);
    if (activePanel) updatePanelProgress(activePanel);
    placeRoutineLayout(section);
  });

  mobileLayoutMq.addEventListener(
    "change",
    () => {
      placeRoutineLayout(section);
    },
    { signal: controller.signal },
  );

  requestAnimationFrame(() => {
    placeRoutineLayout(section);
  });
  panels.forEach((panel) => {
    const scroller = panel.querySelector(SCROLLER_SELECTOR);
    if (scroller instanceof HTMLElement) resizeObserver.observe(scroller);
  });

  tabs.forEach((tab) => {
    tab.addEventListener(
      "click",
      () => {
        selectRoutineTab(section, tab, false, true);
      },
      { signal: controller.signal },
    );

    tab.addEventListener(
      "keydown",
      (event) => {
        if (!(event instanceof KeyboardEvent)) return;

        if (event.key === "Home") {
          event.preventDefault();
          const firstTab = tabs[0];
          if (firstTab) selectRoutineTab(section, firstTab, true, true);
          return;
        }

        if (event.key === "End") {
          event.preventDefault();
          const lastTab = tabs[tabs.length - 1];
          if (lastTab) selectRoutineTab(section, lastTab, true, true);
          return;
        }

        const direction = KEY_TO_DIRECTION[event.key];
        if (!direction) return;

        event.preventDefault();
        selectRoutineTab(
          section,
          getAdjacentTab(tabs, tab, direction),
          true,
          true,
        );
      },
      { signal: controller.signal },
    );
  });
}

/**
 * @param {ParentNode} root
 */
function initRoutineSections(root = document) {
  root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
    if (section instanceof HTMLElement) bindRoutineSection(section);
  });
}

/**
 * @param {EventTarget | null} target
 */
function selectRoutineBlockPanel(target) {
  if (!(target instanceof HTMLElement)) return;

  const section = target.closest(SECTION_SELECTOR);
  if (!(section instanceof HTMLElement)) return;

  const panel = target.matches(PANEL_SELECTOR)
    ? target
    : target.closest(PANEL_SELECTOR);
  if (!(panel instanceof HTMLElement)) return;

  const tab = section.querySelector(
    `${TAB_SELECTOR}[aria-controls="${panel.id}"]`,
  );
  if (tab instanceof HTMLElement) selectRoutineTab(section, tab);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initRoutineSections());
} else {
  initRoutineSections();
}

document.addEventListener("shopify:section:load", (event) => {
  const target = event.target;
  if (target instanceof HTMLElement) initRoutineSections(target);
});

document.addEventListener("shopify:block:select", (event) => {
  selectRoutineBlockPanel(event.target);
});

document.addEventListener("shopify:section:unload", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  target.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
    if (!(section instanceof HTMLElement)) return;
    const controller = sectionControllers.get(section);
    if (!controller) return;

    controller.abort();
    sectionControllers.delete(section);
  });
});
